import { useState, useEffect } from "react";
import { ChevronRight, ChevronDown, BookOpen, Church, Users, Building2, TreePine } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import api from "@/lib/api";

const TreeNode = ({ node, level = 0 }) => {
  const [isOpen, setIsOpen] = useState(level < 3);
  const hasChildren = node.hijos && node.hijos.length > 0;
  const paddingLeft = level * 20;

  const getIconForType = (tipo) => {
    if (!tipo) return <Church className="w-4 h-4" />;
    if (tipo.includes("parroquia") || tipo.includes("capilla")) return <Church className="w-4 h-4" />;
    if (tipo.includes("diocesis") || tipo.includes("arquidiocesis")) return <Building2 className="w-4 h-4" />;
    if (tipo.includes("conferencia")) return <Users className="w-4 h-4" />;
    return <TreePine className="w-4 h-4" />;
  };

  return (
    <div className="select-none">
      <div
        className={`flex items-center gap-2 py-2 px-3 rounded-md cursor-pointer hover:bg-stone-100 transition-colors ${
          level === 0 ? "bg-rose-50" : ""
        }`}
        style={{ paddingLeft: `${paddingLeft + 12}px` }}
        onClick={() => hasChildren && setIsOpen(!isOpen)}
      >
        {hasChildren ? (
          isOpen ? (
            <ChevronDown className="w-4 h-4 text-stone-400 shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-stone-400 shrink-0" />
          )
        ) : (
          <span className="w-4" />
        )}
        <span className={`text-stone-400 shrink-0`}>{getIconForType(node.tipo)}</span>
        <span className={`text-sm ${level === 0 ? "font-serif text-rose-950 font-medium" : "text-stone-700"}`}>
          {node.nombre}
        </span>
        {node.tipo && (
          <Badge variant="outline" className="ml-2 text-xs bg-white">
            {node.tipo.replace(/_/g, " ")}
          </Badge>
        )}
        {node.nivel && (
          <span className="ml-auto text-xs text-stone-400">{node.nivel}</span>
        )}
      </div>
      {node.descripcion && isOpen && (
        <p
          className="text-xs text-stone-500 ml-10 mb-2"
          style={{ paddingLeft: `${paddingLeft + 12}px` }}
        >
          {node.descripcion}
        </p>
      )}
      {node.ejemplos && isOpen && (
        <p
          className="text-xs text-stone-400 italic ml-10 mb-2"
          style={{ paddingLeft: `${paddingLeft + 12}px` }}
        >
          Ej: {node.ejemplos.join(", ")}
        </p>
      )}
      {hasChildren && isOpen && (
        <div>
          {node.hijos.map((child, index) => (
            <TreeNode key={index} node={child} level={level + 1} />
          ))}
        </div>
      )}
    </div>
  );
};

export default function DocumentacionPage() {
  const [jerarquiaJurisdiccional, setJerarquiaJurisdiccional] = useState(null);
  const [jerarquiaFuncional, setJerarquiaFuncional] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [jurisdiccionalRes, funcionalRes] = await Promise.all([
        api.get("/referencia/jerarquia-jurisdiccional"),
        api.get("/referencia/jerarquia-funcional"),
      ]);
      setJerarquiaJurisdiccional(jurisdiccionalRes.data);
      setJerarquiaFuncional(funcionalRes.data);
    } catch (error) {
      console.error("Error loading hierarchies:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-stone-400">Cargando documentación...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="documentacion-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-serif text-rose-950 tracking-tight flex items-center gap-3">
          <BookOpen className="w-8 h-8 text-amber-600" strokeWidth={1.5} />
          Documentación y FAQ
        </h1>
        <p className="text-stone-600 mt-1">
          Información sobre la estructura jerárquica de la Iglesia Católica
        </p>
      </div>

      {/* Introduction Card */}
      <Card className="border-stone-200 bg-gradient-to-r from-rose-50 to-amber-50">
        <CardContent className="p-6">
          <h2 className="text-xl font-serif text-rose-950 mb-3">Sobre ECCLESIA</h2>
          <p className="text-stone-600 leading-relaxed">
            ECCLESIA es una plataforma multitenant diseñada para la gestión integral de servicios
            parroquiales. El sistema está estructurado siguiendo la jerarquía canónica de la Iglesia
            Católica, permitiendo la administración desde el nivel de parroquia hasta el nivel de
            conferencia episcopal.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="p-4 bg-white rounded-lg border border-stone-200">
              <h3 className="font-medium text-stone-800 mb-2">Jerarquía Jurisdiccional</h3>
              <p className="text-sm text-stone-600">
                Establece las líneas de autoridad y gobierno en la Iglesia. Define quién tiene
                potestad sobre qué territorio o fieles.
              </p>
            </div>
            <div className="p-4 bg-white rounded-lg border border-stone-200">
              <h3 className="font-medium text-stone-800 mb-2">Jerarquía Funcional</h3>
              <p className="text-sm text-stone-600">
                Describe las oficinas, ministerios y organismos de apoyo que facilitan la misión
                de la Iglesia sin ser jurisdicciones canónicas.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Hierarchy Tabs */}
      <Tabs defaultValue="jurisdiccional" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2 bg-stone-100">
          <TabsTrigger value="jurisdiccional" className="data-[state=active]:bg-rose-950 data-[state=active]:text-white">
            Jurisdiccional
          </TabsTrigger>
          <TabsTrigger value="funcional" className="data-[state=active]:bg-rose-950 data-[state=active]:text-white">
            Funcional
          </TabsTrigger>
        </TabsList>

        <TabsContent value="jurisdiccional" className="mt-6">
          <Card className="border-stone-200 bg-white">
            <CardHeader>
              <CardTitle className="text-xl font-serif text-stone-800">
                {jerarquiaJurisdiccional?.titulo}
              </CardTitle>
              <p className="text-sm text-stone-500">
                {jerarquiaJurisdiccional?.descripcion}
              </p>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[600px] pr-4">
                {jerarquiaJurisdiccional?.arbol && (
                  <TreeNode node={jerarquiaJurisdiccional.arbol} />
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Tipos de Entidad Reference */}
          <Card className="border-stone-200 bg-white mt-6">
            <CardHeader>
              <CardTitle className="text-lg font-medium text-stone-800">
                Tipos de Entidades Jurisdiccionales
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {jerarquiaJurisdiccional?.tipos_entidad?.map((tipo, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-stone-50 rounded-lg"
                  >
                    <span className="text-sm text-stone-700">{tipo.nombre}</span>
                    <Badge variant="outline" className="bg-white">
                      Nivel {tipo.nivel}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="funcional" className="mt-6">
          <Card className="border-stone-200 bg-white">
            <CardHeader>
              <CardTitle className="text-xl font-serif text-stone-800">
                {jerarquiaFuncional?.titulo}
              </CardTitle>
              <p className="text-sm text-stone-500">
                {jerarquiaFuncional?.descripcion}
              </p>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[600px] pr-4">
                {jerarquiaFuncional?.arbol && (
                  <TreeNode node={jerarquiaFuncional.arbol} />
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* FAQ Section */}
      <Card className="border-stone-200 bg-white">
        <CardHeader>
          <CardTitle className="text-xl font-serif text-stone-800">Preguntas Frecuentes (FAQ)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="font-medium text-stone-800 mb-2">
              ¿Qué es una entidad jurisdiccional?
            </h3>
            <p className="text-sm text-stone-600">
              Una entidad jurisdiccional es una circunscripción eclesiástica con territorio y fieles
              definidos, gobernada por una autoridad eclesiástica. Ejemplos: diócesis, parroquias,
              vicarías foráneas.
            </p>
          </div>

          <div>
            <h3 className="font-medium text-stone-800 mb-2">
              ¿Cuál es la diferencia entre arquidiócesis y diócesis?
            </h3>
            <p className="text-sm text-stone-600">
              Una arquidiócesis es la sede metropolitana de una provincia eclesiástica, gobernada
              por un arzobispo. Las diócesis sufragáneas dependen jerárquicamente de ella, aunque
              cada obispo diocesano gobierna su diócesis con autoridad propia.
            </p>
          </div>

          <div>
            <h3 className="font-medium text-stone-800 mb-2">
              ¿Qué es un decanato o vicaría foránea?
            </h3>
            <p className="text-sm text-stone-600">
              Es una agrupación de parroquias vecinas dentro de una diócesis, presidida por un
              decán o vicario foráneo. Facilita la coordinación pastoral y administrativa entre
              parroquias cercanas.
            </p>
          </div>

          <div>
            <h3 className="font-medium text-stone-800 mb-2">
              ¿Cómo funciona el sistema multitenant de ECCLESIA?
            </h3>
            <p className="text-sm text-stone-600">
              Cada entidad jurisdiccional (parroquia, decanato, diócesis, etc.) puede tener sus
              propios usuarios y datos. Los usuarios de niveles superiores pueden acceder a los
              datos de las entidades bajo su jurisdicción, manteniendo la estructura jerárquica
              de la Iglesia.
            </p>
          </div>

          <div>
            <h3 className="font-medium text-stone-800 mb-2">
              ¿Qué tipos de usuarios existen en el sistema?
            </h3>
            <p className="text-sm text-stone-600">
              El sistema contempla varios roles: Super Admin (administrador global), Admin de
              Conferencia Episcopal, Admin Diocesano, Admin de Vicaría, Párroco, Vicario Parroquial,
              Secretaria y Lector (solo lectura). Cada rol tiene permisos específicos según el
              nivel jerárquico.
            </p>
          </div>

          <div>
            <h3 className="font-medium text-stone-800 mb-2">
              ¿Qué son las cuasi-parroquias y rectorías?
            </h3>
            <p className="text-sm text-stone-600">
              Una cuasi-parroquia es una comunidad de fieles que aún no reúne las condiciones para
              ser erigida como parroquia, pero tiene atención pastoral propia. Una rectoría es una
              iglesia no parroquial confiada a un rector para funciones específicas.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
